import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';
import { WorkspaceBar } from './WorkspaceControls';
import { useTheme } from '../context/ThemeContext';
import { useEntitlements } from '../context/EntitlementsContext';
import { detectTenant } from '../lib/tenantConfig';
import {
  getEnterpriseRole, getRoleTitle, getRoleColor, getHomeRoute,
  isSuperAdmin, isAcademyAdmin, shouldShowEnterpriseNav, shouldShowCoachNav,
  ROLE_KEYS
} from '../lib/enterpriseRoles';
import {
  LayoutDashboard, Users, Calendar, ClipboardList, Trophy,
  Presentation, Video, Settings, LogOut, ChevronDown, Sun, Moon,
  Shield, Plus, Menu, X, Activity, FileText,
  Heart, Apple, WifiOff, Building2,
  Globe, CreditCard, GraduationCap, Bell, BarChart3, Crown, Home, Lock, Wallet,
  Pencil, Check
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from './ui/dropdown-menu';
import NotificationBell from './NotificationBell';
import { InstallMenuItem } from './InstallApp';
import MiCoachLogo from './MiCoachLogo';
import LegalFooter from './LegalFooter';
import SidebarGroup from './SidebarGroup';

// Villarreal Academy badge images
const VA_BADGE = 'https://customer-assets.emergentagent.com/job_560a82f5-d842-4ce7-9063-cdf20d9214d7/artifacts/epzg45go_image.png';

// Enterprise navigation - for Superadmin and Admin Academia
const ENTERPRISE_NAV_SUPERADMIN = [
  { label: 'DIRECCION GLOBAL', items: [
    { path: '/enterprise/superadmin', icon: Globe, label: 'Red Global' },
    { path: '/enterprise/metrics', icon: BarChart3, label: 'Métricas' },
    { path: '/enterprise/formation', icon: GraduationCap, label: 'Formación' },
    { path: '/enterprise/payments', icon: CreditCard, label: 'Pagos' },
    { path: '/enterprise/notifications', icon: Bell, label: 'Comunicaciones' },
  ]},
];

const ENTERPRISE_NAV_ADMIN_ACADEMY = [
  { label: 'MI ACADEMIA', items: [
    { path: '/enterprise/academy', icon: Building2, label: 'Dashboard Academia', dynamic: true },
    { path: '/enterprise/formation', icon: GraduationCap, label: 'Formación' },
    { path: '/enterprise/notifications', icon: Bell, label: 'Comunicaciones' },
  ]},
];

// Coach navigation - full MiCoach operative tools
const COACH_NAV_GROUPS = [
  {
    items: [
      { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { path: '/squad', icon: Users, label: 'Plantilla' },
    ]
  },
  {
    label: 'PLANIFICACIÓN',
    items: [
      { path: '/sesiones', icon: ClipboardList, label: 'Sesiones & Gym' },
      { path: '/microciclo', icon: Calendar, label: 'Microciclo' },
    ]
  },
  {
    label: 'COMPETICIÓN',
    items: [
      { path: '/partidos', icon: Trophy, label: 'Partidos' },
      { path: '/pizarras', icon: Presentation, label: 'Pizarras Tácticas' },
      { path: '/videos', icon: Video, label: 'Vídeos', requiresVideo: true },
      { path: '/estadisticas', icon: BarChart3, label: 'Estadísticas' },
      { path: '/informes', icon: FileText, label: 'Informes' },
    ]
  },
  {
    label: 'SEGUIMIENTO',
    items: [
      { path: '/vestuario', icon: Activity, label: 'Vestuario' },
      { path: '/asistencia', icon: Heart, label: 'Asistencia & Wellness' },
      { path: '/nutricion', icon: GraduationCap, label: 'Aprendizaje' },
    ]
  },
  {
    label: 'GESTIÓN',
    items: [
      { path: '/coordinador', icon: Building2, label: 'Coordinador', onlyCoordinator: true },
      { path: '/caja', icon: Wallet, label: 'Cobros del club', onlyCoordinator: true },
      { path: '/organizacion', icon: Crown, label: 'Mi club' },
      { path: '/squad/staff', icon: Users, label: 'Staff del equipo' },
      { path: '/padres', icon: Users, label: 'Padres' },
    ]
  },
];

const MOBILE_NAV = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Inicio' },
  { path: '/sesiones', icon: ClipboardList, label: 'Sesiones' },
  { path: '/microciclo', icon: Calendar, label: 'Plan' },
  { path: '/partidos', icon: Trophy, label: 'Partidos' },
  { path: '/pizarra', icon: Presentation, label: 'Pizarra' },
];

// Visual metadata for the active-plan badge shown in the sidebar / mobile drawer
const PLAN_META = {
  free:  { label: 'Free',  bg: 'rgba(161,161,170,0.15)', border: 'rgba(161,161,170,0.35)', color: '#a1a1aa' },
  pro:   { label: 'Pro',   bg: 'rgba(var(--primary-rgb),0.12)',   border: 'rgba(var(--primary-rgb),0.35)',   color: 'var(--primary)' },
  club:  { label: 'Club',  bg: 'rgba(168,85,247,0.15)',  border: 'rgba(168,85,247,0.40)',  color: '#c084fc' },
};

const PlanBadge = ({ plan, isAdmin = false, onClick, size = 'sm' }) => {
  const meta = isAdmin ? PLAN_META.club : PLAN_META[(plan || 'free').toLowerCase()] || PLAN_META.free;
  const { trial } = useEntitlements();
  const lg = size === 'lg';
  const isFree = !isAdmin && (plan || 'free').toLowerCase() === 'free';
  const daysLeft = isFree && trial ? trial.trial_days_left : null;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-md font-bold leading-none transition-opacity hover:opacity-80 ${
        lg ? 'px-2 py-1 text-[11px]' : 'mt-1 px-1.5 py-0.5 text-[9px]'
      }`}
      style={{ background: meta.bg, border: `1px solid ${meta.border}`, color: meta.color, fontFamily: 'Inter, sans-serif', letterSpacing: '0.03em' }}
      title={isAdmin ? 'Administrador: acceso completo sin cupos de plan' : daysLeft != null ? (daysLeft > 0 ? `Te quedan ${daysLeft} días de prueba antes de pasar a Pro` : 'Tu prueba ha finalizado') : 'Ver plan y suscripción'}
      data-testid="sidebar-plan-badge"
    >
      <span style={{ width: lg ? 6 : 5, height: lg ? 6 : 5, borderRadius: '50%', background: meta.color, display: 'inline-block' }} />
      {isAdmin ? 'ADMIN · ILIMITADO' : `PLAN ${meta.label.toUpperCase()}`}
      {/* Antes ponía solo "· fin", que no dice nada. */}
      {daysLeft != null && (
        <span style={{ opacity: 0.85, fontWeight: 700 }}>
          · {daysLeft > 0
              ? `${daysLeft} ${daysLeft === 1 ? 'día' : 'días'} de prueba`
              : 'prueba acabada'}
        </span>
      )}
    </button>
  );
};

export const Layout = ({ children }) => {
  const { user, logout, isAdmin, networkError } = useAuth();
  const { teams, currentTeam, selectTeam } = useTeam();
  const { theme, toggleTheme } = useTheme();
  const { canCreate, features, plan } = useEntitlements();
  const navigate = useNavigate();
  const location = useLocation();
  // Do not carry a previous screen's scroll into the next screen behind the
  // fixed phone header. Query/filter changes retain their scroll position.
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const tenant = detectTenant();
  
  // IMPORTANT: Check if we're in enterprise context (tenant exists)
  const inEnterpriseContext = !!tenant;

  // Role-based configuration - PASS inEnterpriseContext to all role functions
  const enterpriseRole = getEnterpriseRole(user, inEnterpriseContext);
  const roleColor = getRoleColor(enterpriseRole);
  const showEnterpriseNav = shouldShowEnterpriseNav(user, inEnterpriseContext);
  const showCoachNav = shouldShowCoachNav(user, inEnterpriseContext);
  const userIsSuperAdmin = isSuperAdmin(user, inEnterpriseContext);
  const userIsAcademyAdmin = isAcademyAdmin(user, inEnterpriseContext);

  // Quien manda es el backend: se lee la feature de /api/entitlements en vez de
  // deducirla del plan cacheado. Antes se comparaba `selected_plan` en crudo (un
  // valor antiguo como 'elite' no casaba y bloqueaba el menú) y se colaba
  // `role === 'admin'`, que enseñaba la sección pero luego el backend daba 403.
  const hasVideoAccess = !!features.video_analysis;
  const isNavItemVisible = (item) => {
    // El admin de plataforma tiene acceso completo; los coordinadores siguen
    // necesitando el rol de su club y la feature concedida por el servidor.
    if (item.onlyCoordinator) {
      const esCoordinador = user?.club_role === 'coordinator'
        || user?.org_role === 'coordinator'
        || user?.role === 'coordinator'
        || isAdmin;
      return esCoordinador && !!features.coordinator_dashboard;
    }
    if (item.requiresVideo) return hasVideoAccess;
    return true;
  };

  // Redirect to correct home on mount if on wrong dashboard
  useEffect(() => {
    if (!user || !tenant) return;
    
    const currentPath = location.pathname;
    const homeRoute = getHomeRoute(user, inEnterpriseContext);
    
    // If superadmin/admin_academy lands on coach dashboard, redirect
    if (showEnterpriseNav && currentPath === '/dashboard') {
      navigate(homeRoute, { replace: true });
    }
  }, [user, tenant, location.pathname, inEnterpriseContext, navigate, showEnterpriseNav]);

  const handleLogout = async () => { await logout(); navigate('/login'); };
  const initial = user?.name?.charAt(0).toUpperCase() || 'U';

  // Get enterprise nav items based on role
  const getEnterpriseNavItems = () => {
    if (userIsSuperAdmin) return ENTERPRISE_NAV_SUPERADMIN;
    if (userIsAcademyAdmin) return ENTERPRISE_NAV_ADMIN_ACADEMY;
    return [];
  };

  // Render enterprise navigation
  const renderEnterpriseNav = () => {
    const navGroups = getEnterpriseNavItems();
    
    return navGroups.map((group, gi) => (
      <div key={`ent-${gi}`}>
        <p className="px-2 mb-1" style={{ 
          fontSize: '0.55rem', 
          fontWeight: 700, 
          letterSpacing: '0.18em', 
          color: `${tenant?.primaryColor || '#FFD700'}60`, 
          fontFamily: 'JetBrains Mono, monospace' 
        }}>
          {group.label}
        </p>
        <div className="space-y-0.5">
          {group.items.map(item => {
            // Handle dynamic paths (e.g., academy detail with ID)
            let itemPath = item.path;
            if (item.dynamic && enterpriseRole === ROLE_KEYS.ADMIN_ACADEMY) {
              itemPath = `/enterprise/academy/${user?.academy_id || ''}`;
            }
            
            return (
              <NavLink key={item.path} to={itemPath}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <item.icon className="w-[14px] h-[14px] flex-shrink-0" />
                <span className="text-xs">{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      </div>
    ));
  };

  // Render coach navigation (only for coaches)
  const renderCoachNav = (onNavigate) => {
    if (!showCoachNav) return null;
    
    return COACH_NAV_GROUPS.map((group, gi) => {
      const visibleItems = group.items.filter(isNavItemVisible);
      
      if (visibleItems.length === 0) return null;
      
      return <SidebarGroup key={gi} label={group.label} items={visibleItems} onNavigate={onNavigate} />;
    });
  };

  return (
    <div className="layout-root flex" style={{ background: 'var(--bg-0)' }} data-testid="main-layout">

      {/* Network banner */}
      {networkError && (
        <div className="fixed top-0 left-0 right-0 z-[100] flex items-center justify-center gap-2 py-1.5 text-xs font-semibold"
          style={{ background: 'rgba(255,77,109,0.9)', color: '#fff', backdropFilter: 'blur(8px)' }}>
          <WifiOff className="w-3.5 h-3.5" /> Sin conexión — Reconectando...
        </div>
      )}

      {/* DESKTOP SIDEBAR */}
      <aside className="hidden md:flex flex-col w-52 glass-sidebar fixed h-full z-40">
        {/* Logo - tenant branded with role badge */}
        <div className="px-4 py-4" style={{ borderBottom: `1px solid ${tenant ? tenant.borderColor : 'var(--border-subtle)'}` }}>
          {tenant ? (
            <div className="flex items-center gap-2.5">
              <img src={tenant.logoUrl} alt={tenant.name} className="w-8 h-8 rounded-lg flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-black text-sm leading-none truncate" style={{ fontFamily: 'Inter, sans-serif', color: tenant.primaryColor }}>{tenant.sidebarTitle}</p>
                <p className="text-[8px] mt-0.5" style={{ color: 'var(--text-2)' }}>Powered by MiCoach</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2.5">
              <MiCoachLogo size={32} variant="auto" />
              <div className="min-w-0">
                <p className="font-black text-sm leading-none" style={{ fontFamily: 'Inter, sans-serif', color: 'var(--text-0)' }}>MiCoach</p>
                <p className="text-[9px] mt-0.5" style={{ color: 'var(--text-2)' }}>Plataforma Entrenadores</p>
                <PlanBadge plan={plan} isAdmin={isAdmin} onClick={() => navigate('/ajustes?tab=subscription')} />
              </div>
            </div>
          )}
          
          {/* Role badge - only show for enterprise users */}
          {tenant && showEnterpriseNav && (
            <div className="mt-3 flex items-center gap-2 px-2 py-1.5 rounded-lg" 
              style={{ background: roleColor.bg, border: `1px solid ${roleColor.border}` }}>
              <Crown className="w-3 h-3" style={{ color: roleColor.primary }} />
              <span className="text-[9px] font-bold truncate" style={{ color: roleColor.primary }}>
                {getRoleTitle(enterpriseRole, true)}
              </span>
            </div>
          )}
        </div>

        {/* Team selector - only for coaches */}
        {showCoachNav && (
          <div className="px-3 py-2" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg text-xs font-medium"
                  style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid var(--border-subtle)', color: 'var(--text-1)' }}
                  data-testid="team-selector">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Shield className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--primary)' }} />
                    <span className="truncate">{currentTeam?.name || 'Seleccionar equipo'}</span>
                  </div>
                  <ChevronDown className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--text-2)' }} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-48"
                style={{ background: 'var(--surface-1)', border: '1px solid var(--border-strong)', borderRadius: '0.75rem', boxShadow: 'var(--shadow-lg)' }}>
                {teams.map(t => (
                  <DropdownMenuItem key={t.id} onClick={() => selectTeam(t)}
                    className="text-xs cursor-pointer"
                    style={{ color: currentTeam?.id === t.id ? 'var(--primary)' : 'var(--text-1)', background: currentTeam?.id === t.id ? 'var(--primary-soft)' : 'transparent' }}
                    data-testid={`team-option-${t.id}`}>
                    <Shield className="w-3 h-3 mr-1.5" />
                    <span className="truncate flex-1">{t.name}</span>
                    {currentTeam?.id === t.id && <span style={{ color: 'var(--primary)' }}>✓</span>}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator style={{ background: 'var(--border-subtle)' }} />
                {currentTeam && (
                  <DropdownMenuItem onClick={() => navigate(`/equipos/${currentTeam.id}/editar`)}
                    className="text-xs cursor-pointer" style={{ color: 'var(--text-1)' }}
                    data-testid="edit-team-btn">
                    <Pencil className="w-3 h-3 mr-1.5" /> Editar este equipo
                  </DropdownMenuItem>
                )}
                {canCreate('teams') ? (
                  <DropdownMenuItem onClick={() => navigate('/equipos/nuevo')}
                    className="text-xs cursor-pointer" style={{ color: 'var(--primary)' }}
                    data-testid="create-team-btn">
                    <Plus className="w-3 h-3 mr-1.5" /> Crear equipo
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => navigate('/ajustes?tab=subscription')}
                    className="text-xs cursor-pointer" style={{ color: 'var(--text-2)' }}
                    data-testid="create-team-locked">
                    <Lock className="w-3 h-3 mr-1.5" /> Crear equipo · Pro
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {/* Navigation groups */}
        <nav aria-label="Navegación principal" className="flex-1 px-2 py-2 overflow-y-auto space-y-1">
          {/* Enterprise nav (Superadmin / Admin Academia) */}
          {showEnterpriseNav && renderEnterpriseNav()}

          {/* Divider between enterprise and coach nav for admins who might need both */}
          {showEnterpriseNav && userIsSuperAdmin && (
            <div className="py-2">
              <div style={{ height: 1, background: 'var(--border-subtle)' }} />
            </div>
          )}

          {/* Coach nav - only for coaches */}
          {renderCoachNav()}

          {/* Admin panel link */}
          {isAdmin && (
            <div>
              <p className="px-2 mb-1" style={{ fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.18em', color: 'var(--text-2)', fontFamily: 'JetBrains Mono, monospace' }}>ADMIN</p>
              <NavLink to="/admin" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} data-testid="sidebar-nav-admin">
                <Activity className="w-[14px] h-[14px] flex-shrink-0" />
                <span className="text-xs">Panel Admin</span>
              </NavLink>
            </div>
          )}
        </nav>

        {/* User section */}
        <div className="p-2.5" style={{ borderTop: '1px solid var(--border-subtle)' }}>
          {/* Notification Bell */}
          <div className="mb-2">
            <NotificationBell />
          </div>
          
          <button
            onClick={toggleTheme}
            className="flex items-center gap-3 w-full px-3 py-2 rounded-xl text-sm font-medium transition-all mb-1"
            style={{ color: 'var(--text-1)' }}
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            {theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
          </button>
          
          <div className="flex items-center gap-2">
            <NavLink to="/ajustes"
              className="flex items-center gap-2 flex-1 min-w-0 rounded-lg p-1.5 -m-1.5"
              style={{ transition: 'background-color 200ms' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              data-testid="sidebar-nav-settings">
              <div className="w-7 h-7 rounded-full flex items-center justify-center font-black text-xs flex-shrink-0"
                style={{ background: roleColor.bg, border: `1px solid ${roleColor.border}`, color: roleColor.primary, fontFamily: 'Inter, sans-serif' }}>
                {initial}
              </div>
              <div className="min-w-0 truncate">
                <p className="text-xs font-semibold truncate" style={{ color: 'var(--text-0)' }}>{user?.name}</p>
                <p className="text-[9px]" style={{ color: roleColor.primary }}>{isAdmin ? 'Administrador' : user?.role === 'coordinator' ? 'Coordinador' : getRoleTitle(enterpriseRole, true)}</p>
              </div>
            </NavLink>
            <button onClick={handleLogout}
              className="p-1.5 rounded-lg flex-shrink-0"
              style={{ color: 'var(--text-2)', transition: 'color 200ms, background-color 200ms' }}
              onMouseEnter={e => { e.currentTarget.style.color = '#ff4d6d'; e.currentTarget.style.backgroundColor = 'rgba(255,77,109,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-2)'; e.currentTarget.style.backgroundColor = 'transparent'; }}
              title="Cerrar sesión" data-testid="logout-btn">
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </aside>

      {/* MOBILE HEADER */}
      <header className="mobile-header md:hidden">
        <div className="flex-1 flex items-center gap-2.5 min-w-0 mr-2">
          {tenant ? (
            <img src={VA_BADGE} alt="VA" className="w-7 h-7 rounded-lg flex-shrink-0" />
          ) : (
            <MiCoachLogo size={28} variant="auto" />
          )}
          <div className="min-w-0">
            <p className="font-black text-sm leading-tight truncate" style={{ fontFamily: 'Inter, sans-serif', color: 'var(--text-0)' }}>
              {tenant ? 'Villarreal Academy' : (currentTeam?.name || 'MiCoach')}
            </p>
            <p className="text-[10px] leading-tight capitalize" style={{ color: 'var(--text-2)' }}>
              {new Date().toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <NotificationBell />
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
            title={mobileOpen ? 'Cerrar menú' : 'Menú'}
            className="p-2 rounded-xl transition-all active:scale-90"
            style={{ color: 'var(--text-2)', background: mobileOpen ? 'rgba(255,255,255,0.08)' : 'transparent' }}>
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Mobile Drawer */}
      <AnimatePresence>
      {mobileOpen && (
        <motion.div className="md:hidden fixed inset-0 z-40" key="mobile-drawer"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.22 }}>
          <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={() => setMobileOpen(false)} />
          <motion.div className="absolute top-0 right-0 w-[80%] max-w-sm h-full glass-sidebar overflow-y-auto"
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'tween', ease: 'easeOut', duration: 0.28 }}>
            <div className="p-4 space-y-3" style={{ paddingTop: 'calc(var(--mobile-header-h) + env(safe-area-inset-top, 0px) + 1rem)' }}>
              {/* Role badge in mobile */}
              {tenant && showEnterpriseNav && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg mb-4"
                  style={{ background: roleColor.bg, border: `1px solid ${roleColor.border}` }}>
                  <Crown className="w-4 h-4" style={{ color: roleColor.primary }} />
                  <span className="text-xs font-bold" style={{ color: roleColor.primary }}>
                    {getRoleTitle(enterpriseRole, true)}
                  </span>
                </div>
              )}

              {/* Active plan badge (mobile) - only when not in tenant mode */}
              {!tenant && (
                <div className="mb-2">
                  <PlanBadge
                    plan={plan}
                    isAdmin={isAdmin}
                    size="lg"
                    onClick={() => { setMobileOpen(false); navigate('/ajustes?tab=subscription'); }}
                  />
                </div>
              )}
              
              {/* Equipos (móvil) — antes solo existía en la barra lateral de
                  escritorio, así que desde el teléfono no se podía cambiar
                  de equipo ni crear uno nuevo. */}
              {showCoachNav && (
                <div className="pb-3 mb-1" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <p className="px-2 mb-1.5" style={{ fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.18em', color: 'var(--text-2)', fontFamily: 'JetBrains Mono, monospace' }}>
                    EQUIPO
                  </p>
                  <div className="space-y-1">
                    {teams.map(t => {
                      const active = currentTeam?.id === t.id;
                      return (
                        <button key={t.id}
                          onClick={() => { selectTeam(t); setMobileOpen(false); }}
                          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-left"
                          style={{
                            background: active ? 'var(--primary-soft)' : 'rgba(255,255,255,0.035)',
                            border: `1px solid ${active ? 'var(--primary)' : 'var(--border-subtle)'}`,
                            color: active ? 'var(--primary)' : 'var(--text-1)',
                          }}
                          data-testid={`mobile-team-option-${t.id}`}>
                          <Shield className="w-4 h-4 flex-shrink-0" />
                          <span className="text-xs font-semibold truncate flex-1">{t.name}</span>
                          {active && <Check className="w-4 h-4 flex-shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex gap-2 mt-2">
                    {currentTeam && (
                      <button
                        onClick={() => { setMobileOpen(false); navigate(`/equipos/${currentTeam.id}/editar`); }}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold"
                        style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid var(--border-subtle)', color: 'var(--text-1)' }}
                        data-testid="mobile-edit-team-btn">
                        <Pencil className="w-3.5 h-3.5" /> Editar
                      </button>
                    )}
                    {canCreate('teams') ? (
                      <button
                        onClick={() => { setMobileOpen(false); navigate('/equipos/nuevo'); }}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold"
                        style={{ background: 'var(--primary)', color: 'var(--primary-ink)' }}
                        data-testid="mobile-create-team-btn">
                        <Plus className="w-3.5 h-3.5" /> Nuevo equipo
                      </button>
                    ) : (
                      <button
                        onClick={() => { setMobileOpen(false); navigate('/ajustes?tab=subscription'); }}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold"
                        style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid var(--border-subtle)', color: 'var(--text-2)' }}
                        data-testid="mobile-create-team-locked">
                        <Lock className="w-3.5 h-3.5" /> Nuevo equipo · Pro
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Mobile enterprise nav */}
              {showEnterpriseNav && getEnterpriseNavItems().map((group, gi) => (
                <div key={`m-ent-${gi}`}>
                  <p className="px-2 mb-1" style={{ fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.18em', color: `${tenant?.primaryColor || '#FFD700'}60`, fontFamily: 'JetBrains Mono, monospace' }}>{group.label}</p>
                  {group.items.map(item => {
                    let itemPath = item.path;
                    if (item.dynamic && enterpriseRole === ROLE_KEYS.ADMIN_ACADEMY) {
                      itemPath = `/enterprise/academy/${user?.academy_id || ''}`;
                    }
                    return (
                      <NavLink key={item.path} to={itemPath} onClick={() => setMobileOpen(false)}
                        className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                        <item.icon className="w-4 h-4 flex-shrink-0" />
                        <span className="text-xs">{item.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              ))}
              
              {/* Mobile coach nav */}
              {renderCoachNav(() => setMobileOpen(false))}
              {isAdmin && <NavLink to="/admin" onClick={() => setMobileOpen(false)} className="nav-item">
                <Activity className="w-4 h-4" /><span className="text-xs">Panel Admin</span>
              </NavLink>}
              
              {/* Instalar como PWA. Solo aparece si NO se está entrando ya
                  como app instalada: es la vía fiable en iPhone, donde el
                  navegador no ofrece instalar por su cuenta. */}
              <InstallMenuItem
                onNavigate={() => setMobileOpen(false)}
                wrapperClassName="pt-3 border-t"
                wrapperStyle={{ borderColor: 'var(--border-subtle)' }}
              />

              <div className="pt-3 border-t flex items-center justify-between" style={{ borderColor: 'var(--border-subtle)' }}>
                <button onClick={toggleTheme} className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-1)' }}>
                  {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                  {theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
                </button>
                <NavLink to="/ajustes" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-2)' }}>
                  <Settings className="w-4 h-4" />Ajustes
                </NavLink>
                <button onClick={handleLogout} className="flex items-center gap-2 text-xs" style={{ color: 'var(--danger)' }}>
                  <LogOut className="w-4 h-4" />Salir
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>

      {/* MAIN */}
      <main className="flex-1 min-w-0 md:ml-52 mobile-main md:!pt-0 md:!pb-0">
        <div className="p-4 md:p-6 max-w-[1400px] mx-auto"><WorkspaceBar />{children}</div>
        <div className="max-w-[1400px] mx-auto">
          <LegalFooter />
        </div>
      </main>

      {/* MOBILE BOTTOM NAV - only for coaches */}
      {showCoachNav && (
        <nav className={`mobile-nav md:hidden${mobileOpen ? ' is-hidden' : ''}`}>
          {MOBILE_NAV.map(item => (
            <NavLink key={item.path} to={item.path}
              className={({ isActive }) => `mobile-nav-item${isActive ? ' active' : ''}`}
              style={({ isActive }) => ({ color: isActive ? 'var(--primary)' : 'var(--text-2)' })}>
              {({ isActive }) => (
                <>
                  <item.icon style={{ width: isActive ? 26 : 22, height: isActive ? 26 : 22, transition: 'width 150ms, height 150ms' }} />
                  <span style={{ fontSize: 10, fontWeight: isActive ? 700 : 500, letterSpacing: isActive ? '-0.01em' : '0' }}>{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
      )}

      {/* MOBILE BOTTOM NAV - for enterprise users */}
      {showEnterpriseNav && (
        <nav className={`mobile-nav md:hidden${mobileOpen ? ' is-hidden' : ''}`}>
          {[
            { path: userIsSuperAdmin ? '/enterprise/superadmin' : `/enterprise/academy/${user?.academy_id || ''}`, icon: Home, label: 'Inicio' },
            { path: '/enterprise/formation', icon: GraduationCap, label: 'Formación' },
            { path: '/enterprise/notifications', icon: Bell, label: 'Avisos' },
            ...(userIsSuperAdmin ? [{ path: '/enterprise/metrics', icon: BarChart3, label: 'Metricas' }] : []),
          ].map(item => (
            <NavLink key={item.path} to={item.path}
              className={({ isActive }) => `mobile-nav-item${isActive ? ' active' : ''}`}
              style={({ isActive }) => ({
                color: isActive ? (tenant?.primaryColor || '#FFD700') : 'var(--text-2)',
                background: isActive ? `${tenant?.primaryColor || '#FFD700'}20` : undefined,
                borderColor: isActive ? `${tenant?.primaryColor || '#FFD700'}40` : undefined,
              })}>
              {({ isActive }) => (
                <>
                  <item.icon style={{ width: isActive ? 26 : 22, height: isActive ? 26 : 22, transition: 'width 150ms, height 150ms' }} />
                  <span style={{ fontSize: 10, fontWeight: isActive ? 700 : 500 }}>{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
};
